import { useMemo } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { WeeklyGoalItem } from "@/components/ui/goals/weekly-goal-item"
import { Target, BookOpen, Gamepad2, Rocket, Calculator, ClipboardList } from "lucide-react"
import { StorageService } from "@/hooks/services/storageService"
import { AuthStore } from "@/hooks/stores/AuthStore"
import { useNavigate } from "react-router"

const GOAL_TYPE_ICON: Record<string, React.ElementType> = {
  complete_modules: BookOpen,
  watch_videos: BookOpen,
  play_games: Gamepad2,
  path_start: Rocket,
  complete_assessment: ClipboardList,
  log_activity: ClipboardList,
  default: Target,
}

const GOAL_TYPE_ICON_COLOR: Record<string, string> = {
  play_games: "text-emerald-600",
  complete_modules: "text-indigo-600",
  watch_videos: "text-violet-600",
  path_start: "text-amber-600",
  complete_assessment: "text-rose-600",
  log_activity: "text-teal-600",
  default: "text-indigo-600",
}

const GOAL_TYPE_BG: Record<string, string> = {
  play_games: "bg-emerald-100",
  complete_modules: "bg-indigo-100",
  watch_videos: "bg-violet-100",
  path_start: "bg-amber-100",
  complete_assessment: "bg-rose-100",
  log_activity: "bg-teal-100",
  default: "bg-indigo-100",
}

export function WeeklyGoalsSection() {
  const navigate = useNavigate()
  const userId = AuthStore((s) => s.currentUser?._id)

  const weeklyGoals = useMemo(() => {
    if (!userId) return []
    const all = StorageService.read("goals")
    if (!Array.isArray(all)) return []
    return all
      .filter((g: any) => g.userId === userId && g.status !== "completed" && (g.progress ?? 0) < 100)
      .sort((a: any, b: any) => {
        const priorityOrder: Record<string, number> = { high: 0, medium: 1, low: 2 }
        return (priorityOrder[a.priority] ?? 1) - (priorityOrder[b.priority] ?? 1)
      })
      .slice(0, 3)
  }, [userId])

  if (weeklyGoals.length === 0) {
    return (
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-xl text-slate-900">Weekly Goals</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center mb-3">
              <Target className="w-6 h-6 text-slate-400" />
            </div>
            <p className="text-sm font-semibold text-slate-700 mb-1">No active goals</p>
            <p className="text-xs text-slate-500 mb-4 max-w-xs">
              Add career paths to auto-generate weekly goals, or create your own below.
            </p>
            <button
              onClick={() => navigate("/career-explorer")}
              className="text-xs font-bold text-indigo-600 hover:text-indigo-800 transition-colors"
            >
              Explore career paths
            </button>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="border-slate-200 shadow-sm">
      <CardHeader>
        <CardTitle className="text-xl text-slate-900">Active Goals</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {weeklyGoals.map((goal: any) => {
          const iconType = goal.goalType ?? "default"
          const IconComp = GOAL_TYPE_ICON[iconType] ?? GOAL_TYPE_ICON.default
          const iconColor = GOAL_TYPE_ICON_COLOR[iconType] ?? GOAL_TYPE_ICON_COLOR.default
          const iconBg = GOAL_TYPE_BG[iconType] ?? GOAL_TYPE_BG.default

          return (
            <WeeklyGoalItem
              key={goal.id}
              icon={<IconComp className={`w-8 h-8 ${iconColor}`} />}
              iconBg={iconBg}
              title={goal.title}
              description={goal.description ?? ""}
              progress={goal.progress ?? 0}
              completed={goal.currentValue ?? 0}
              total={goal.targetValue ?? 1}
            />
          )
        })}
      </CardContent>
    </Card>
  )
}
